/**
 * PieceDraftEditor — TipTap-based HTML editor for piece drafts.
 *
 * S-094/095/096 / FEAT-007 (Phase 4). Replaces the legacy plain-textarea
 * DraftEditor for inline piece-body editing. Reads HTML, writes HTML via
 * the `onChange` prop. Caller owns the debounced save + dirty-state
 * tracking; this component is purely a controlled editor surface.
 *
 * Architecture decisions:
 *   - ADR-007: HTML is the canonical draft format. The editor is fed HTML
 *     from `piece_drafts.content` and emits HTML back through `onChange`.
 *   - ADR-008: TipTap (ProseMirror-based, headless). StarterKit + Link.
 *     No custom extensions in v1. Heading levels restricted to H2/H3
 *     (no H1) to match the sanitizer's SAFE_TAGS allowlist.
 *
 * The sanitizer (apps/api/src/lib/sanitize.ts) is the server-side safety
 * net on PATCH /drafts/:id. The editor's onChange output is the input to
 * that mutation. No client-side sanitization needed — but the editor's
 * allowed-tag set deliberately matches the server allowlist so the
 * round-trip is clean (no content lost to sanitization).
 *
 * S-096 (toolbar) lives in the same file as a sibling component below.
 * Buttons mirror StarterKit + Link commands and reflect their active
 * state via `editor.isActive(name)`. Keyboard shortcuts (Cmd+B, Cmd+I,
 * Cmd+Z) come for free with StarterKit's history extension.
 */

import { useEffect } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

interface Props {
  /** Initial HTML content. Updates flow through this prop only on
   * editor mount or when the prop identity changes (e.g., the user
   * navigates to a different piece). */
  value: string;
  /** Called on every editor update with the latest `editor.getHTML()`.
   * The caller is responsible for debouncing the save. */
  onChange: (html: string) => void;
  /** Read-only flag. When true the editor surface is non-editable
   * and the toolbar buttons are disabled. */
  disabled?: boolean;
}

export function PieceDraftEditor({ value, onChange, disabled }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer" },
      }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "piece-draft-content",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Keep the editor's editable state in sync with the `disabled` prop.
  // TipTap doesn't watch this from its initial config; setEditable is
  // the supported runtime toggle.
  useEffect(() => {
    if (!editor) return;
    // BUG-109 — emitUpdate=false: going read-only during a save is not an
    // edit, and TipTap's default update here restarted the save, forever.
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  // If the consumer swaps `value` to a different piece's content
  // (e.g., navigation between drafts), reset the editor body. We
  // intentionally do NOT do this on every value change — only when the
  // value diverges from what the editor currently shows. This avoids
  // clobbering the user's in-flight edits during debounced saves.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current === value) return;
    // Heuristic: only reset when the editor is not focused. A focused
    // editor implies the user is actively typing; resetting would lose
    // their caret position.
    if (editor.isFocused) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  return (
    <div className="piece-draft-editor">
      <PieceDraftToolbar editor={editor} disabled={disabled} />
      <EditorContent editor={editor} />
    </div>
  );
}

// ---------- Toolbar ----------

interface ToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
}

function PieceDraftToolbar({ editor, disabled }: ToolbarProps) {
  if (!editor) {
    return <div className="piece-draft-toolbar" aria-hidden="true" />;
  }

  const isActive = (name: string, attrs?: Record<string, unknown>) =>
    editor.isActive(name, attrs);

  const onLink = () => {
    const existing = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL (leave blank to remove)", existing ?? "https://");
    if (url === null) return; // user cancelled
    if (url.trim() === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <div className="piece-draft-toolbar" role="toolbar" aria-label="Formatting">
      <ToolbarButton
        label="B"
        title="Bold (Cmd/Ctrl+B)"
        bold
        active={isActive("bold")}
        disabled={disabled || !editor.can().toggleBold()}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="I"
        title="Italic (Cmd/Ctrl+I)"
        italic
        active={isActive("italic")}
        disabled={disabled || !editor.can().toggleItalic()}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <span className="piece-draft-toolbar-sep" aria-hidden="true" />
      <ToolbarButton
        label="H2"
        title="Heading 2"
        active={isActive("heading", { level: 2 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
      />
      <ToolbarButton
        label="H3"
        title="Heading 3"
        active={isActive("heading", { level: 3 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
      />
      <span className="piece-draft-toolbar-sep" aria-hidden="true" />
      <ToolbarButton
        label="•"
        title="Bullet list"
        active={isActive("bulletList")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="1."
        title="Ordered list"
        active={isActive("orderedList")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        label="&ldquo;"
        title="Blockquote"
        active={isActive("blockquote")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <span className="piece-draft-toolbar-sep" aria-hidden="true" />
      <ToolbarButton
        label="</>"
        title="Inline code"
        active={isActive("code")}
        disabled={disabled || !editor.can().toggleCode()}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <ToolbarButton
        label="link"
        title="Add or remove link"
        active={isActive("link")}
        disabled={disabled}
        onClick={onLink}
      />
      <span className="piece-draft-toolbar-sep" aria-hidden="true" />
      <ToolbarButton
        label="↶"
        title="Undo (Cmd/Ctrl+Z)"
        disabled={disabled || !editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolbarButton
        label="↷"
        title="Redo (Cmd/Ctrl+Shift+Z)"
        disabled={disabled || !editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  bold?: boolean;
  italic?: boolean;
  onClick: () => void;
}

function ToolbarButton({
  label,
  title,
  active,
  disabled,
  bold,
  italic,
  onClick,
}: ToolbarButtonProps) {
  const className = [
    "piece-draft-toolbar-btn",
    active ? "is-active" : "",
    bold ? "is-bold" : "",
    italic ? "is-italic" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={className}
      title={title}
      aria-label={title}
      aria-pressed={active ? true : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      dangerouslySetInnerHTML={{ __html: label }}
    />
  );
}
